'use client';

import { useState } from 'react';

interface ContactInfo {
  name: string;
  parish: string;
  municipality: string;
  position: string;
  email: string;
  phone: string;
  employeeCount: string;
  voterCount: string;
}

interface ContactRequestFormProps {
  contactInfo: ContactInfo;
  profile: string;
  onClose: () => void;
}

export default function ContactRequestForm({ contactInfo, profile, onClose }: ContactRequestFormProps) {
  const [message, setMessage] = useState('');
  const [preferredContact, setPreferredContact] = useState<'email' | 'phone'>('email');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'contact',
          results: {
            contactInfo,
            totalScore: 0,
            profile,
            areaScores: {},
            pressurePoints: [],
          },
          message,
          preferredContact,
        }),
      });
      if (!response.ok) {
        console.error('Contact request failed:', await response.text());
      }
    } catch (err) {
      console.error('Contact request error:', err);
    }
    setIsSubmitting(false);
    setSubmitted(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-form-title"
    >
      <div
        className="bg-white w-full max-w-md border border-gray-100 max-h-[90vh] overflow-y-auto"
        style={{ borderRadius: '5px', boxShadow: '0 16px 40px -8px rgba(0,0,0,0.25)' }}
      >
        {/* Teal top line */}
        <div className="h-1" style={{ backgroundColor: '#009688' }} />

        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-5"
          style={{ borderBottom: '1px solid #F0F0F0' }}
        >
          <div className="flex items-center gap-2.5">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 014.69 13a19.79 19.79 0 01-3.07-8.67A2 2 0 013.6 2.18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.91 9.91a16 16 0 006.06 6.06l1.79-1.79a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
            </svg>
            <h2
              id="contact-form-title"
              className="font-semibold text-gray-900"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.15rem', fontWeight: 700 }}
            >
              Pedido de Contacto
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 transition-colors"
            style={{ borderRadius: '5px' }}
            aria-label="Fechar formulário"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-6">
              <div
                className="w-14 h-14 flex items-center justify-center mx-auto mb-4"
                style={{ borderRadius: '5px', border: '1px solid rgba(46, 125, 50, 0.3)', backgroundColor: 'rgba(46, 125, 50, 0.05)' }}
              >
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#2E7D32" aria-hidden="true">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h3
                className="font-semibold text-gray-900 mb-2"
                style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.2rem', fontWeight: 700 }}
              >
                Pedido Submetido
              </h3>
              <p
                className="text-sm text-gray-500 mb-6 leading-relaxed"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                A nossa equipa entrará em contacto consigo nas próximas 24 horas úteis para dar seguimento ao seu diagnóstico.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="h-10 px-6 text-white font-semibold text-sm transition-all duration-250 hover:opacity-90"
                style={{ fontFamily: 'DM Sans, sans-serif', borderRadius: '5px', backgroundColor: '#009688', border: '2px solid #009688' }}
              >
                Fechar
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {/* Pre-filled info */}
              <div
                className="p-4 mb-5"
                style={{ borderRadius: '5px', backgroundColor: '#F9F9F9', border: '1px solid #F0F0F0' }}
              >
                <p
                  className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2.5"
                  style={{ fontFamily: 'DM Sans, sans-serif', letterSpacing: '0.08em' }}
                >
                  Dados do Contacto
                </p>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-400" style={{ fontFamily: 'DM Sans, sans-serif' }}>Nome:</span>
                    <span className="ml-1 font-semibold text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>{contactInfo.name}</span>
                  </div>
                  <div>
                    <span className="text-gray-400" style={{ fontFamily: 'DM Sans, sans-serif' }}>Cargo:</span>
                    <span className="ml-1 font-semibold text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>{contactInfo.position}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-gray-400" style={{ fontFamily: 'DM Sans, sans-serif' }}>Junta:</span>
                    <span className="ml-1 font-semibold text-gray-900" style={{ fontFamily: 'DM Sans, sans-serif' }}>{contactInfo.parish}</span>
                  </div>
                </div>
              </div>

              {/* Preferred contact method */}
              <div className="mb-4">
                <label
                  className="font-semibold text-sm text-gray-700 block mb-2"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  Método de Contacto Preferido
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {(['email', 'phone'] as const).map((method) => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => setPreferredContact(method)}
                      className="h-10 font-semibold text-sm flex items-center justify-center gap-2 transition-all duration-150"
                      style={{
                        fontFamily: 'DM Sans, sans-serif',
                        borderRadius: '5px',
                        border: preferredContact === method ? '2px solid #009688' : '2px solid #F0F0F0',
                        backgroundColor: preferredContact === method ? '#F0FAFA' : 'white',
                        color: preferredContact === method ? '#009688' : '#1A1A1A',
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        {method === 'email' ? (
                          <>
                            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                            <polyline points="22,6 12,13 2,6"/>
                          </>
                        ) : (
                          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 014.69 13a19.79 19.79 0 01-3.07-8.67A2 2 0 013.6 2.18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L7.91 9.91a16 16 0 006.06 6.06l1.79-1.79a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                        )}
                      </svg>
                      {method === 'email' ? 'Email' : 'Telefone'}
                    </button>
                  ))}
                </div>
                <p
                  className="text-xs text-gray-400 mt-1.5"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {preferredContact === 'email'
                    ? `Contactaremos via: ${contactInfo.email}`
                    : `Contactaremos via: ${contactInfo.phone}`}
                </p>
              </div>

              {/* Message */}
              <div className="mb-5">
                <label
                  htmlFor="contact-message"
                  className="font-semibold text-sm text-gray-700 block mb-2"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  Mensagem Adicional{' '}
                  <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <textarea
                  id="contact-message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  placeholder="Descreva brevemente a sua situação ou questões específicas que gostaria de abordar..."
                  className="w-full px-3 py-2.5 border border-gray-200 bg-white text-gray-900 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:border-transparent resize-none transition-all duration-150"
                  style={{ fontFamily: 'DM Sans, sans-serif', borderRadius: '5px' }}
                />
              </div>

              {/* RGPD notice */}
              <p
                className="text-xs text-gray-400 mb-5 leading-relaxed"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Ao submeter este pedido, consente com o tratamento dos seus dados pessoais para fins de contacto, em conformidade com o RGPD. Os seus dados não serão partilhados com terceiros.
              </p>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full h-11 text-white font-semibold text-sm transition-all duration-250 flex items-center justify-center gap-2 active:scale-98 disabled:opacity-70 disabled:cursor-not-allowed"
                style={{
                  fontFamily: 'DM Sans, sans-serif',
                  borderRadius: '5px',
                  backgroundColor: '#009688',
                  border: '2px solid #009688',
                }}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    A enviar...
                  </>
                ) : (
                  <>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <line x1="22" y1="2" x2="11" y2="13"/>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                    Submeter Pedido
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}